import * as borogove from "borogove";
import Picker from "@emoji-mart/react";
import { Button, Message } from "@chatscope/chat-ui-kit-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Menu, MenuItem, ListItemIcon, Popover } from "@mui/material";
import { HTMLAttributes, useEffect, useState } from "react";
import {
	faFaceSmile,
	faPenToSquare,
	faReply,
	faShareNodes,
	faSquareCaretDown,
} from "@fortawesome/free-solid-svg-icons";

const shareData = (m: borogove.ChatMessage) => {
	const data: ShareData = { text: m.body().toPlainText() };
	if (m.attachments.length === 1) {
		data.url = m.attachments[0].uris[0];
	} else if (m.attachments.length < 1 && m.linkMetadata.length === 1) {
		data.url = m.linkMetadata[0].url;
	} else if (m.attachments.length > 1) {
		for (const attach of m.attachments) {
			data.text += "\n" + attach.uris[0];
		}
	}
	return data;
};

const share = (m: borogove.ChatMessage) => {
	const data = shareData(m);
	if (navigator.canShare && navigator.canShare(data)) {
		navigator.share(data);
	} else {
		window.open(
			"https://shareopenly.org/share/?text=" +
				encodeURIComponent(data.text ?? "") +
				"&url=" +
				encodeURIComponent(data.url ?? ""),
		);
	}
};

export const MessageMenuInChat = ({
	m,
	chat,
	setSelectedThread,
	setReplyTo,
	editMessage,
	...rest
}: HTMLAttributes<HTMLDivElement> & {
	m: borogove.ChatMessage;
	chat: borogove.Chat;
	setSelectedThread: (id: string | null) => void;
	setReplyTo: (m: borogove.ChatMessage) => void;
	editMessage: (m: borogove.ChatMessage) => void;
}) => {
	const [reactionPopover, setReactionPopover] = useState<HTMLElement | null>(
		null,
	);
	const [menu, setMenu] = useState<HTMLElement | null>(null);
	const closeMenu = () => {
		setMenu(null);
	};

	useEffect(() => {
		// Force popover to recalculate position
		window.dispatchEvent(new CustomEvent("resize", {}));
	}, [reactionPopover]);

	return (
		<Message.Header className={menu ? "active" : ""} {...rest}>
			<Button
				aria-haspopup="menu"
				title="Open message menu"
				icon={<FontAwesomeIcon icon={faSquareCaretDown} />}
				onClick={(e) => setMenu(e.currentTarget)}
			/>
			<Menu open={!!menu} anchorEl={menu} onClose={closeMenu}>
				<MenuItem
					onClick={() => {
						setReplyTo(m);
						setSelectedThread(m.threadId);
						closeMenu();
					}}
				>
					<ListItemIcon>
						<FontAwesomeIcon icon={faReply} />
					</ListItemIcon>
					Reply
				</MenuItem>
				<MenuItem
					onClick={() => {
						setReactionPopover(menu);
						closeMenu();
					}}
				>
					<ListItemIcon>
						<FontAwesomeIcon icon={faFaceSmile} />
					</ListItemIcon>
					React
				</MenuItem>
				{!m.isIncoming() && (
					<MenuItem
						onClick={() => {
							editMessage(m);
							closeMenu();
						}}
					>
						<ListItemIcon>
							<FontAwesomeIcon icon={faPenToSquare} />
						</ListItemIcon>
						Edit
					</MenuItem>
				)}
				<MenuItem
					onClick={() => {
						share(m);
						closeMenu();
					}}
				>
					<ListItemIcon>
						<FontAwesomeIcon icon={faShareNodes} />
					</ListItemIcon>
					Share
				</MenuItem>
			</Menu>
			<Popover
				open={!!reactionPopover}
				anchorEl={reactionPopover}
				onClose={() => setReactionPopover(null)}
			>
				<Picker
					data={async () => ({ ...(await import("@emoji-mart/data")) })}
					maxFrequentRows={1}
					onEmojiSelect={(emoji: { native: string }) => {
						chat.addReaction(m, borogove.Reaction.unicode(emoji.native));
						setReactionPopover(null);
					}}
				/>
			</Popover>
		</Message.Header>
	);
};
